/**
 * COMPTABILITÉ — LE SEUL ENDROIT OÙ UNE SOMME DEVIENT DU TEXTE.
 *
 * Le dépôt contenait déjà trois formatages monétaires divergents, dans trois
 * unités différentes (centimes, millicentimes, euros), dont deux ne rendaient
 * même pas le même texte : `style: 'currency'` produit une espace insécable
 * étroite que la concaténation d'un « € » ne produit pas. L'un d'eux avait
 * déjà affiché « 150 k€ » pour une soirée à 1 500 € — faux de deux ordres de
 * grandeur, parce que l'unité d'entrée avait été supposée.
 *
 * Ici, l'unité est explicite et unique : LE CENTIME ENTIER. C'est l'unité du
 * domaine en base (`*_cents bigint`), et rien ne se convertit avant l'affichage.
 *
 * `apps/web` n'a aucune dépendance interne — ce fichier ne peut donc pas
 * réutiliser le helper du cœur, il en est le miroir assumé côté navigateur.
 */

/** Le pilote PostgreSQL rend un `bigint` en CHAÎNE : on l'accepte tel quel. */
export type Cents = number | string | null | undefined

function enNombre(c: Cents): number | null {
  if (c === null || c === undefined) return null
  const n = typeof c === 'string' ? Number(c) : c
  return Number.isFinite(n) ? n : null
}

const FMT = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const FMT_ENTIER = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })

/**
 * « 13 474,50 € ». Une valeur absente rend un tiret cadratin, jamais « 0 € » :
 * ne pas savoir et valoir zéro sont deux choses différentes, et les confondre
 * dans un tableau financier fait disparaître une donnée manquante.
 */
export function formatMoney(c: Cents, opts?: { compact?: boolean; signe?: boolean }): string {
  const n = enNombre(c)
  if (n === null) return '—'
  const euros = n / 100
  const prefixe = opts?.signe && n > 0 ? '+' : ''
  if (opts?.compact && Math.abs(euros) >= 10_000) {
    return `${prefixe}${FMT_ENTIER.format(Math.round(euros / 1000))} k€`
  }
  return `${prefixe}${FMT.format(euros)} €`
}

/** L'écart, avec son signe et sa classe de couleur. Zéro se dit « 0,00 € ✓ ». */
/**
 * Le ton d'un écart TOTAL contre les seuils des réglages — pas contre son
 * signe : +1 021 € au-delà du seuil d'alerte n'est pas une bonne nouvelle
 * verte (recette du 07/10/2026). Les écarts de LIGNE gardent leur signe.
 */
export function tonSeuil(c: Cents, seuils: { warn: number; alert: number }): 'calme' | 'tiede' | 'fort' | 'nul' {
  const n = enNombre(c)
  if (n === null || n === 0) return 'nul'
  const a = Math.abs(n)
  return a >= seuils.alert ? 'fort' : a >= seuils.warn ? 'tiede' : 'calme'
}

export function formatEcart(c: Cents): { texte: string; ton: 'nul' | 'positif' | 'negatif' } {
  const n = enNombre(c)
  if (n === null) return { texte: '—', ton: 'nul' }
  if (n === 0) return { texte: '0,00 €', ton: 'nul' }
  return { texte: formatMoney(n, { signe: true }), ton: n > 0 ? 'positif' : 'negatif' }
}

/**
 * Lit une saisie humaine et rend des centimes. Accepte « 13474,50 »,
 * « 13 474.50 », « 13474 ». Rend `null` si ce n'est pas un nombre — jamais 0,
 * qui serait un montant faux plutôt qu'une saisie refusée.
 */
export function parseMoney(saisie: string): number | null {
  const nettoye = saisie.replace(/\s| | |€/g, '').replace(',', '.')
  if (nettoye === '' || !/^-?\d+(\.\d{0,2})?$/.test(nettoye)) return null
  return Math.round(Number(nettoye) * 100)
}

/** Somme de centimes, en entiers : aucune addition ne passe par un flottant. */
export function sommeCents(valeurs: Cents[]): number {
  return valeurs.reduce<number>((t, v) => t + (enNombre(v) ?? 0), 0)
}
